import { listConversations } from "./messaging";
import { myApplications } from "./jobs";
import { myBids, laneLabel } from "./loads";
import { getVerificationStatus } from "./verification";
import { labelize } from "./format";

// A notifications feed derived from live data — no dedicated backend. Each item
// has a stable id so read-state (localStorage, see notifRead) survives reloads.
export type NotifKind = "message" | "application" | "job" | "system";

export interface NotificationItem {
  id: string;
  kind: NotifKind;
  title: string;
  body: string;
  at?: string;
  href: string;
}

export function appTitle(status: string, title?: string | null): string {
  const t = title ?? "a job";
  switch (status) {
    case "accepted": return `You were accepted for ${t}`;
    case "shortlisted": return "Your application was shortlisted";
    case "rejected": return `Update on your application to ${t}`;
    case "reviewing": return `Your application to ${t} is under review`;
    default: return `Applied to ${t}`;
  }
}

export function bidTitle(status: string, lane: string): string {
  switch (status) {
    case "awarded": return `Your bid on ${lane} was awarded`;
    case "viewed": return `Your bid on ${lane} was viewed`;
    case "rejected":
    case "not_selected": return `Your bid on ${lane} was not selected`;
    default: return `Your bid on ${lane}`;
  }
}

export async function buildNotifications(
  profileId: string,
  flags: { isDriver: boolean; isBidder: boolean },
): Promise<NotificationItem[]> {
  const out: NotificationItem[] = [];
  const safe = async (fn: () => Promise<void>) => {
    try {
      await fn();
    } catch {
      /* best effort — one source failing shouldn't blank the feed */
    }
  };

  await safe(async () => {
    const { conversations } = await listConversations();
    for (const c of conversations) {
      // Only notify about messages the OTHER person sent — not my own replies.
      if (c.last_message_from_me) continue;
      out.push({ id: `msg-${c.id}`, kind: "message", title: `New message from ${c.other_name}`, body: c.last_message ?? "", at: c.last_message_at, href: `/messages/${c.id}` });
    }
  });

  if (flags.isDriver) await safe(async () => {
    const { applications } = await myApplications(profileId);
    for (const a of applications) {
      out.push({ id: `app-${a.id}`, kind: "application", title: appTitle(a.status, a.title), body: a.poster_name ? `at ${a.poster_name}` : "", at: a.created_at, href: "/applications" });
    }
  });

  if (flags.isBidder) await safe(async () => {
    const { bids } = await myBids(profileId);
    for (const b of bids) {
      out.push({ id: `bid-${b.id}`, kind: "job", title: bidTitle(b.status, laneLabel(b.origin ?? "—", b.destination ?? "—")), body: `$${b.amount.toLocaleString()}`, at: b.created_at, href: "/bids" });
    }
  });

  await safe(async () => {
    const v = await getVerificationStatus();
    if (v.identity.status !== "verified") {
      out.push({ id: "sys-identity", kind: "system", title: "Verify your identity", body: "Complete identity verification to post, apply, and bid.", href: "/verify" });
    }
    for (const c of v.credentials) {
      if (c.status === "needs_info" || c.status === "rejected") {
        out.push({ id: `sys-${c.id}`, kind: "system", title: `Verification update: ${labelize(c.doc_type)}`, body: `Your ${labelize(c.doc_type)} needs attention.`, href: "/verify" });
      }
    }
  });

  return out.sort((a, b) => (b.at ? +new Date(b.at) : 0) - (a.at ? +new Date(a.at) : 0));
}
